import { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import SendIcon from '@mui/icons-material/Send';

type ClarificationCardProps = {
  question: string;
  onAnswer: (answer: string) => void;
  onSkip: () => void;
  /** Barnläge: större touch-targets, rundare hörn, större text. Default false. */
  child?: boolean;
};

const ClarificationCard = ({
  question,
  onAnswer,
  onSkip,
  child = false,
}: ClarificationCardProps) => {
  const [answer, setAnswer] = useState('');

  const handleSubmit = () => {
    if (answer.trim()) {
      onAnswer(answer.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Stack spacing={2} py={2}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          bgcolor: 'primary.50',
          borderRadius: child ? '14px' : 2,
          p: child ? 2.5 : 2,
          border: '1px solid',
          borderColor: 'primary.100',
        }}
      >
        {child ? (
          <HelpOutlineRoundedIcon color="primary" sx={{ mt: 0.25, flexShrink: 0, fontSize: 28 }} />
        ) : (
          <HelpOutlineIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
        )}
        <Stack spacing={0.5}>
          <Typography
            variant="subtitle2"
            fontWeight={child ? 700 : 600}
            color={child ? 'primary.dark' : 'primary.main'}
            sx={child ? { fontSize: 17 } : undefined}
          >
            Behöver lite hjälp
          </Typography>
          <Typography variant="body2" sx={child ? { fontSize: 17, lineHeight: 1.6 } : undefined}>
            {question}
          </Typography>
        </Stack>
      </Box>

      <TextField
        fullWidth
        multiline
        maxRows={3}
        placeholder="Skriv ditt svar..."
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        onKeyDown={handleKeyDown}
        sx={
          child
            ? {
                '& .MuiOutlinedInput-root': { borderRadius: '14px', fontSize: 16, minHeight: 52 },
              }
            : undefined
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleSubmit}
                disabled={!answer.trim()}
                color="primary"
                edge="end"
                size={child ? 'large' : 'medium'}
                sx={child ? { width: 48, height: 48 } : undefined}
              >
                <SendIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        variant="text"
        size="small"
        onClick={onSkip}
        sx={{
          alignSelf: 'center',
          ...(child && { minHeight: 44, fontSize: 15, fontWeight: 600 }),
        }}
      >
        Hoppa över
      </Button>
    </Stack>
  );
};

export default ClarificationCard;
